import React, { useState, useMemo } from 'react';
import { useSelectionOrder } from '../../../context/SelectionOrderContext';
import { ShoppingCartIcon, BeakerIcon, ArchiveBoxIcon, CheckCircleIcon, ArrowPathIcon, InformationCircleIcon, ExclamationTriangleIcon, MinusCircleIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../../Common/Loading/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const SelectionOrder = () => {
  const {
    selection,
    setSelection,
    loading,
    error,
    orderType,
    setOrderType,
    orderUnitaire,
    orderCapsule,
    selectionId,
    calculateUnitPrice
  } = useSelectionOrder();

  const navigate = useNavigate();
  const [isOrdering, setIsOrdering] = useState(false);
  const [selectedMixturesToRemove, setSelectedMixturesToRemove] = useState([]);

  // Calcul du nombre de mélanges physiques et effluences
  const physicalMixturesCount = useMemo(() => {
    if (!selection?.mixtures) return 0;
    return selection.mixtures.filter(mix =>
      mix.category?.toLowerCase() !== 'effluences'
    ).length;
  }, [selection]);

  const effluencesMixturesCount = useMemo(() => {
    if (!selection?.mixtures) return 0;
    return selection.mixtures.filter(mix =>
      mix.category?.toLowerCase() === 'effluences'
    ).length;
  }, [selection]);

  // Limites maximales pour les mélanges
  const MAX_PHYSICAL_MIXTURES = 15;
  const MAX_EMOTIONAL_MIXTURES = 5;

  // Vérification si les limites sont dépassées
  const isPhysicalLimitExceeded = physicalMixturesCount > MAX_PHYSICAL_MIXTURES;
  const isEmotionalLimitExceeded = effluencesMixturesCount > MAX_EMOTIONAL_MIXTURES;
  const isCapsuleOrderValid = !isPhysicalLimitExceeded && !isEmotionalLimitExceeded;

  // Nombre de mélanges à supprimer
  const physicalMixturesToRemove = isPhysicalLimitExceeded ? physicalMixturesCount - MAX_PHYSICAL_MIXTURES : 0;
  const emotionalMixturesToRemove = isEmotionalLimitExceeded ? effluencesMixturesCount - MAX_EMOTIONAL_MIXTURES : 0;

  // Fonction pour gérer la sélection des mélanges à supprimer
  const toggleMixtureRemoval = (mixtureId) => {
    if (selectedMixturesToRemove.includes(mixtureId)) {
      setSelectedMixturesToRemove(selectedMixturesToRemove.filter(id => id !== mixtureId));
    } else {
      setSelectedMixturesToRemove([...selectedMixturesToRemove, mixtureId]);
    }
  };

  // Fonction pour supprimer les mélanges sélectionnés
  const removeMixtures = () => {
    // TODO: Implémenter la logique pour supprimer les mélanges de la sélection
    // Cette fonction nécessite une modification du contexte SelectionOrderContext
    toast.success(`${selectedMixturesToRemove.length} mélanges ont été supprimés de votre sélection.`);
    const updatedSelection = {
      ...selection,
      mixtures: selection.mixtures.filter(mixture => !selectedMixturesToRemove.includes(mixture.id))
    };
    setSelection(updatedSelection);
    setSelectedMixturesToRemove([]);

    // Simulons un message d'erreur si non implémenté
    toast.info("Cette fonctionnalité n'est pas encore implémentée. Veuillez retourner au catalogue pour modifier votre sélection.");
  };

  if (loading) return (
    <div className="container mx-auto px-4 py-20">
      <div className="flex flex-col items-center justify-center h-full max-w-lg mx-auto text-center">
        <div className="bg-white rounded-xl shadow-sm p-8 w-full">
          <div className="flex justify-center mb-6">
            <div className="bg-primary/10 p-3 rounded-full">
              <ShoppingCartIcon className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Préparation de votre commande</h2>
          <p className="text-gray-600 mb-6">Nous chargeons les informations nécessaires pour votre commande...</p>
          <LoadingSpinner style="medium" />
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-xl mx-auto">
        <div className="bg-red-50 rounded-xl shadow-sm p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-red-100 p-3 rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-red-600">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-red-800 mb-4">Erreur lors du chargement</h2>
          <p className="text-red-600 mb-6">{error.message || "Impossible de charger les informations de sélection"}</p>
          <button
            onClick={() => navigate('/melanges/catalogue')}
            className="btn btn-primary"
          >
            Retour au catalogue
          </button>
        </div>
      </div>
    </div>
  );

  // Vérifier les restrictions pour la commande de capsule
  const handleOrder = async () => {
    if (!selection?.mixtures?.length) {
      return;
    }

    // Vérifier les limites pour la capsule sur mesure
    if (orderType === 'capsule' && !isCapsuleOrderValid) {
      if (isPhysicalLimitExceeded && isEmotionalLimitExceeded) {
        toast.error(`Votre sélection dépasse les limites autorisées pour les mélanges physiques (max: ${MAX_PHYSICAL_MIXTURES}) et effluences (max: ${MAX_EMOTIONAL_MIXTURES}).`);
      } else if (isPhysicalLimitExceeded) {
        toast.error(`Votre sélection contient trop de mélanges physiques (${physicalMixturesCount}/${MAX_PHYSICAL_MIXTURES} maximum).`);
      } else if (isEmotionalLimitExceeded) {
        toast.error(`Votre sélection contient trop de mélanges effluences (${effluencesMixturesCount}/${MAX_EMOTIONAL_MIXTURES} maximum).`);
      }
      return;
    }

    setIsOrdering(true);
    try {
      if (orderType === 'unitaire') {
        await orderUnitaire();
      } else {
        await orderCapsule();
      }
    } finally {
      setIsOrdering(false);
    }
  };

  const goToMixtures = () => {
    navigate('/melanges/catalogue');
  };

  const hasMixtures = selection?.mixtures?.length > 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-base-100 rounded-xl shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-primary/10 p-3 rounded-full mr-4">
                <ShoppingCartIcon className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold">Commander votre sélection</h1>
            </div>
          </div>
          
          {/* ID de sélection */}
          {/* <div className="mt-6 flex items-start">
            <div className="bg-base-200 rounded-lg p-4 flex-1">
              <div className="flex items-start">
                <InformationCircleIcon className="h-6 w-6 text-blue-500 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-blue-800 mb-1">Identifiant de sélection</h3>
                  <div className="flex items-center">
                    <code className="bg-base-100 px-3 py-1 rounded border border-blue-200 text-blue-700 font-mono text-sm mr-2">
                      {selectionId}
                    </code>
                    <button 
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => {
                        navigator.clipboard.writeText(selectionId);
                        toast.success('Identifiant copié !');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-blue-600 text-sm mt-2">
                    Cet identifiant sera utilisé pour tracer votre commande. Conservez-le précieusement.
                  </p>
                </div>
              </div>
            </div>
          </div> */}
        </div>
        
        {!hasMixtures && (
          <div className="bg-base-100 rounded-xl shadow-sm p-8 text-center my-8">
            <div className="flex justify-center mb-6">
              <div className="bg-yellow-100 p-3 rounded-full">
                <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" />
              </div>
            </div>
            <h2 className="text-xl font-bold mb-4">Aucun mélange sélectionné</h2>
            <p className="mb-6 max-w-md mx-auto">
              Pour créer votre commande, vous devez d'abord sélectionner au moins un mélange dans notre catalogue.
            </p>
            <div className="flex justify-center space-x-4">
              <button 
                onClick={goToMixtures} 
                className="btn btn-primary flex items-center"
              >
                <BeakerIcon className="h-5 w-5 mr-2" />
                Parcourir le catalogue
              </button>
            </div>
            <div className="mt-8 pt-6 border-t border-base-200">
              <h3 className="text-sm font-medium mb-3">Comment sélectionner des mélanges ?</h3>
              <ol className="text-sm text-left max-w-md mx-auto space-y-2">
                <li className="flex items-start">
                  <span className="bg-primary/10 text-primary font-medium h-5 w-5 rounded-full flex items-center justify-center text-xs mr-2 mt-0.5">1</span>
                  <span>Allez dans le catalogue de mélanges</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-primary/10 text-primary font-medium h-5 w-5 rounded-full flex items-center justify-center text-xs mr-2 mt-0.5">2</span>
                  <span>Cochez les mélanges que vous souhaitez commander</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-primary/10 text-primary font-medium h-5 w-5 rounded-full flex items-center justify-center text-xs mr-2 mt-0.5">3</span>
                  <span>Cliquez sur "Commander la sélection" en haut de la page</span>
                </li>
              </ol>
            </div>
          </div>
        )}
        
        {hasMixtures && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Colonne de gauche - Liste des mélanges */}
            <div className="lg:col-span-1">
              <div className="bg-base-100 rounded-xl shadow-sm overflow-hidden">
                <div className="p-5">
                  <h2 className="text-xl font-semibold mb-6 flex items-center">
                    <BeakerIcon className="h-6 w-6 mr-2 text-primary" />
                    Mélanges sélectionnés
                  </h2>
                  
                  <div className="space-y-4">
                    {selection.mixtures.map((mixture, index) => {
                      // Déterminer le type de mélange
                      const isPhysical = mixture.category?.toLowerCase() !== 'effluences';
                      const isEffluence = mixture.category?.toLowerCase() === 'effluences';
                      const mixtureTypeColor = isPhysical ? 'bg-primary/60' : isEffluence ? 'bg-blue-400' : 'bg-gray-400';
                      const mixtureTypeText = isPhysical ? 'Physique' : isEffluence ? 'Effluence' : 'Autre';
                      
                      // Détermine si ce mélange peut être supprimé (basé sur le dépassement des limites)
                      const canBeRemoved = (isPhysical && isPhysicalLimitExceeded) || (isEffluence && isEmotionalLimitExceeded);
                      
                      return (
                        <div key={index} className="bg-base-200 border border-base-300 rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-medium mb-1 flex items-center">
                                <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-1 rounded mr-2">
                                  #{index + 1}
                                </span>
                                {mixture.name}
                              </h3>
                              <div className="flex flex-wrap gap-2 mt-2">
                                <span className={`text-white text-xs px-2 py-1 rounded flex items-center ${mixtureTypeColor}`}>
                                  <div className="h-2 w-2 bg-white rounded-full mr-1"></div>
                                  {mixtureTypeText}
                                </span>
                                <span className="bg-base-300 text-xs px-2 py-1 rounded">
                                  Dilution: {mixture.dilution}
                                </span>
                                <span className="bg-base-300 text-xs px-2 py-1 rounded">
                                  {mixture.subCategory}
                                </span>
                              </div>
                            </div>
                            
                            {/* Bouton de suppression pour les mélanges qui dépassent la limite en mode capsule */}
                            {orderType === 'capsule' && canBeRemoved && (
                              <div className="ml-2 flex flex-col items-end">
                                <button 
                                  onClick={() => toggleMixtureRemoval(mixture.id)}
                                  className={`rounded-full p-1 ${selectedMixturesToRemove.includes(mixture.id)
                                    ? 'bg-red-100 text-red-600' 
                                    : 'bg-base-300 hover:bg-red-50 hover:text-red-500'
                                  }`}
                                  title="Supprimer ce mélange"
                                >
                                  <MinusCircleIcon className="h-5 w-5" />
                                </button>
                                {selectedMixturesToRemove.includes(mixture.id) && (
                                  <span className="text-xs text-red-600 mt-1">À supprimer</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Bouton pour confirmer la suppression des mélanges sélectionnés */}
                  {orderType === 'capsule' && (isPhysicalLimitExceeded || isEmotionalLimitExceeded) && selectedMixturesToRemove.length > 0 && (
                    <div className="mt-4">
                      <button
                        onClick={removeMixtures}
                        className="w-full py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center"
                      >
                        <MinusCircleIcon className="h-5 w-5 mr-2" />
                        Supprimer {selectedMixturesToRemove.length} mélange(s) sélectionné(s)
                      </button>
                    </div>
                  )}
                  
                  <div className="mt-6">
                    <div className="flex items-center justify-between border-t border-base-300 pt-4">
                      <span className="text-sm">Nombre total de mélanges:</span>
                      <span className="text-sm font-semibold bg-primary/10 text-primary px-3 py-1 rounded-full">
                        {selection.mixtures.length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Colonne de droite - Options de commande */}
            <div>
              <div className="bg-base-100 rounded-xl shadow-sm overflow-hidden sticky top-6">
                <div className="p-5">
                  <h2 className="text-xl font-semibold mb-6 flex items-center">
                    <ShoppingCartIcon className="h-6 w-6 mr-2 text-primary" />
                    Type de commande
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      <button
                        type="button"
                        className={`flex items-center justify-between w-full p-4 rounded-lg transition-all ${orderType === 'unitaire'
                          ? 'bg-primary/10 border border-primary shadow-sm' 
                          : 'bg-base-100 border border-base-300 hover:bg-base-200'
                        }`}
                        onClick={() => setOrderType('unitaire')}
                      >
                        <div className="flex items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${orderType === 'unitaire' ? 'bg-primary/20 text-primary' : 'bg-base-200'
                          }`}>
                            <BeakerIcon className="h-5 w-5" />
                          </div>
                          <div className="ml-3 text-left">
                            <span className={`font-medium ${orderType === 'unitaire' ? 'text-primary' : ''}`}>
                              Testeurs à l'unité
                            </span>
                            <p className="text-xs mt-1">
                              Un seul testeur contenant tous vos mélanges sélectionnés
                            </p>
                          </div>
                        </div>
                        {orderType === 'unitaire' && <CheckCircleIcon className="h-5 w-5 text-primary" />}
                      </button>
                      
                      <button
                        type="button"
                        className={`flex items-center justify-between w-full p-4 rounded-lg transition-all ${orderType === 'capsule'
                          ? 'bg-primary/10 border border-primary shadow-sm' 
                          : 'bg-base-100 border border-base-300 hover:bg-base-200'
                        }`}
                        onClick={() => setOrderType('capsule')}
                      >
                        <div className="flex items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${orderType === 'capsule' ? 'bg-primary/20 text-primary' : 'bg-base-200'
                          }`}>
                            <ArchiveBoxIcon className="h-5 w-5" />
                          </div>
                          <div className="ml-3 text-left">
                            <span className={`font-medium ${orderType === 'capsule' ? 'text-primary' : ''}`}>
                              Capsule sur mesure
                            </span>
                            <p className="text-xs mt-1">
                              Une capsule personnalisée avec tous vos mélanges
                            </p>
                          </div>
                        </div>
                        {orderType === 'capsule' && <CheckCircleIcon className="h-5 w-5 text-primary" />}
                      </button>
                    </div>
                    
                    {orderType === 'unitaire' && (
                      <div className="mt-6 rounded-lg border border-primary/20 bg-primary/5 p-4">
                        <h3 className="text-sm font-medium text-primary mb-2 flex items-center">
                          <InformationCircleIcon className="h-5 w-5 mr-1" />
                          Comment fonctionnent les testeurs à l'unité ?
                        </h3>
                        <p className="text-xs mb-2">
                          Vous recevrez <strong>un testeur pour chaque mélange</strong> que vous avez sélectionné.
                          Le prix unitaire varie selon le nombre total de mélanges commandés.
                        </p>
                        
                        <div className="mt-4 mb-2">
                          <p className="text-sm font-medium mb-2">Prix unitaire:</p>
                          <div className="grid grid-cols-3 gap-2">
                            <div className={`rounded-lg border p-3 text-center ${selection.mixtures.length < 10 ? 'bg-primary text-white border-primary' : 'bg-base-200 border-base-300'
                            }`}>
                              <p className="text-lg font-bold">{selection.mixtures.length < 10 ? '✓' : ''} 4,00€</p>
                              <p className="text-xs font-medium">1 à 9</p>
                            </div>
                            <div className={`rounded-lg border p-3 text-center ${selection.mixtures.length >= 10 && selection.mixtures.length < 20 ? 'bg-primary text-white border-primary' : 'bg-base-200 border-base-300'
                            }`}>
                              <p className="text-lg font-bold">{selection.mixtures.length >= 10 && selection.mixtures.length < 20 ? '✓' : ''} 3,50€</p>
                              <p className="text-xs font-medium">10 à 19</p>
                            </div>
                            <div className={`rounded-lg border p-3 text-center ${selection.mixtures.length >= 20 ? 'bg-primary text-white border-primary' : 'bg-base-200 border-base-300'
                            }`}>
                              <p className="text-lg font-bold">{selection.mixtures.length >= 20 ? '✓' : ''} 3,00€</p>
                              <p className="text-xs font-medium">20 et +</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {orderType === 'capsule' && (
                      <div className="mt-6 rounded-lg border border-primary/20 bg-primary/5 p-4">
                        <h3 className="text-sm font-medium text-primary mb-2 flex items-center">
                          <InformationCircleIcon className="h-5 w-5 mr-1" />
                          Contraintes pour la capsule sur mesure
                        </h3>
                        <p className="text-xs mb-3">
                          Pour des raisons techniques, le nombre de mélanges dans une capsule est limité :
                        </p>
                        
                        <div className="space-y-3 mb-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center">
                                <div className="h-2 w-2 bg-primary/60 rounded-full mr-2"></div>
                                <span className="text-sm font-medium">Physiques :</span>
                              </div>
                              <p className="text-xs mt-1">{MAX_PHYSICAL_MIXTURES} maximum par mélange</p>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isPhysicalLimitExceeded ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                              {physicalMixturesCount} / {MAX_PHYSICAL_MIXTURES}
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center">
                                <div className="h-2 w-2 bg-blue-400 rounded-full mr-2"></div>
                                <span className="text-sm font-medium">Émotionnels :</span>
                              </div>
                              <p className="text-xs mt-1">{MAX_EMOTIONAL_MIXTURES} maximum par mélange</p>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isEmotionalLimitExceeded ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                              {effluencesMixturesCount} / {MAX_EMOTIONAL_MIXTURES}
                            </div>
                          </div>
                        </div>
                        
                        {(!isCapsuleOrderValid) && (
                          <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3">
                            <div className="flex items-start">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-red-500 mt-0.5 mr-2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                              </svg>
                              <div>
                                <p className="text-sm font-medium text-red-700">Limite dépassée</p>
                                <p className="text-xs text-red-600 mt-1">
                                  {isPhysicalLimitExceeded && `Vous avez ${physicalMixturesToRemove} mélanges physiques en trop. `}
                                  {isEmotionalLimitExceeded && `Vous avez ${emotionalMixturesToRemove} mélanges effluences en trop. `}
                                  Sélectionnez les mélanges à supprimer à l'aide des icônes "-" dans la liste.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="mt-6">
                      <div className="bg-base-200 p-5 rounded-lg">
                        {/* Détail des mélanges par type */}
                        {orderType === 'unitaire' && <div className="mb-4 border-b border-base-300 pb-3">
                          <h3 className="text-sm font-medium mb-2">Détail des mélanges :</h3>
                          
                          <div className="flex justify-between items-center text-xs mb-2">
                            <div className="flex items-center">
                              <div className="h-2 w-2 bg-primary/60 rounded-full mr-2"></div>
                              <span>Mélanges physiques</span>
                            </div>
                            <span className="font-medium">{physicalMixturesCount}</span>
                          </div>
                          
                          <div className="flex justify-between items-center text-xs mb-2">
                            <div className="flex items-center">
                              <div className="h-2 w-2 bg-blue-400 rounded-full mr-2"></div>
                              <span>Mélanges effluences</span>
                            </div>
                            <span className="font-medium">{effluencesMixturesCount}</span>
                          </div>
                          
                          <div className="flex justify-between items-center text-xs mt-3 pt-2 border-t border-dashed border-base-300">
                            <span className="font-medium">Total</span>
                            <span className="font-semibold">{selection.mixtures.length}</span>
                          </div>
                        </div>}
                        
                        {/* Prix unitaire selon le type de commande */}
                        {orderType === 'unitaire' && (
                          <div className="flex justify-between items-center text-sm mb-3">
                            <span>Prix unitaire:</span>
                            <span className="font-semibold">{calculateUnitPrice(selection.mixtures.length).toFixed(2)}€</span>
                          </div>
                        )}
                        
                        {/* Total de la commande */}
                        <div className="border-t border-base-300 my-3 pt-3">
                          <div className="flex justify-between items-center">
                            <span className="text-lg font-medium">Total:</span>
                            <span className="text-xl font-bold text-primary">
                              {orderType === 'unitaire' 
                                ? `${(selection.mixtures.length * calculateUnitPrice(selection.mixtures.length)).toFixed(2)}€`
                                : '45,00€'
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <button
                      type="button"
                      className={`mt-6 w-full py-4 px-4 font-semibold text-lg rounded-lg transition-all flex items-center justify-center ${orderType === 'capsule' && !isCapsuleOrderValid
                        ? 'bg-gray-400 text-white cursor-not-allowed opacity-70' 
                        : 'bg-primary text-white hover:bg-primary-dark'
                      }`}
                      onClick={handleOrder}
                      disabled={isOrdering || (orderType === 'capsule' && !isCapsuleOrderValid)}
                    >
                      {isOrdering ? (
                        <>
                          <ArrowPathIcon className="animate-spin h-5 w-5 mr-2" />
                          Création en cours...
                        </>
                      ) : (
                        <>
                          <ShoppingCartIcon className="h-6 w-6 mr-3" />
                          Commander maintenant
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectionOrder; 